import { NModalLayoutRight } from '../../../../common/modal/layout-right/modal-layout-right.component';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core';
import { Catalog } from '@df/catalog/catalog.interfaces';
import { ISizeName } from '@df/core/service/client';
import { NDfModal } from '@df/ui/modal/df-modal.interface';
import { ModalRef } from '@df/ui/modal/modal-ref';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { DfCmsModal } from '@df/ui/modal/df-cms-modal.decorator';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { PriceComponent } from '@df/ui/price/price.component';
import { MODAL_DATA } from '@df/ui/modal/modal.service';
import isEmpty from 'lodash-es/isEmpty';

export namespace NProductViewPriceListModal {
    export const BLOCK_ID = 'product-view-price-list-modal';

    export interface IBlockData extends Catalog.IBlockData<IBlockDataContent> {}

    export interface IBlockDataContent extends NICmsContentComponent.IData {
        title?: string;
        tableTitle?: string;
        rows?: ITableRow[];
    }

    export interface ITableRow {
        size: string;
        weight: string;
        price: number;
    }
}

@DfCmsModal({
    id: NProductViewPriceListModal.BLOCK_ID,
    name: 'Product View Price List Modal',
    position: (sizeName: ISizeName) => (sizeName === 'S' ? 'bottom' : 'right'),
    config: sizeName => ({
        ...NModalLayoutRight.getConfig(sizeName),
        width: NModalLayoutRight.getWidth(sizeName),
        height: sizeName === 'S' ? '85dvh' : '100dvh',
        position:
            sizeName === 'S'
                ? { bottom: '0' }
                : {
                      top: '0',
                      right: '0',
                      bottom: '0'
                  },
        hasBackdrop: true
    }),
    autoCreate: true,
    reopenPolicy: NDfModal.ReopenPolicy.last,
    sizeRestriction: sizeId => (['S', 'M'].includes(sizeId) ? ['S', 'M'] : ['L', 'X']),
    prerenderAllowed: false
})
@ICmsComponent<ProductViewPriceListModalComponent>({
    id: NProductViewPriceListModal.BLOCK_ID,
    block: {
        url: NProductViewPriceListModal.BLOCK_ID,
        autoload: 'bootstrap'
    },
    onDemandOnly: true,
    name: 'Product View Price List Modal',
    description: 'Product View Price List Modal',
    fields: [
        {
            key: 'title',
            type: NICmsContentField.EFieldType.text,
            title: 'Title',
            default: 'Price List'
        },
        {
            key: 'tableTitle',
            type: NICmsContentField.EFieldType.text,
            title: 'Table Title'
        },
        <NICmsContentField.IRepeaterField>{
            key: 'rows',
            type: NICmsContentField.EFieldType.repeater,
            title: 'Table Rows',
            itemName: 'Row',
            minSize: 0,
            fields: [
                {
                    key: 'size',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Size'
                },
                {
                    key: 'weight',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Weight'
                },
                {
                    key: 'price',
                    type: NICmsContentField.EFieldType.number,
                    title: 'Price'
                }
            ]
        }
    ]
})
@Component({
    selector: 'product-view-price-list-modal',
    templateUrl: './product-view-price-list-modal.html',
    styleUrl: './product-view-price-list-modal.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [ICmsClientModule, TranslationModule, CommonModule, PriceComponent]
})
export class ProductViewPriceListModalComponent extends UiComponent implements NICmsContentComponent.IComponent, OnInit {
    @ICmsContent({
        type: 'blockContent'
    })
    content?: NProductViewPriceListModal.IBlockDataContent;

    protected readonly headings = ['Sizes', 'Approximate Weight (Lbs.)', 'Price Ea.'];
    protected readonly cells = ['size', 'weight', 'price'];
    protected readonly modalData: { table?: string } = inject(MODAL_DATA);

    private readonly modalRef = inject(ModalRef<ProductViewPriceListModalComponent>);

    protected close(): void {
        this.modalRef.close();
    }

    get tableRows(): NProductViewPriceListModal.ITableRow[] {
        return this.content?.rows?.filter(row => !isEmpty(row)) || [];
    }
}
